import { Layer } from 'konva/lib/Layer';
import { toRaw } from 'vue';
import { findNode, moveNode } from '../../graph/graphModel.js';
import { resolveNodePorts } from '../../Hasp/properties/v2/graphPorts.js';
import { buildGraphScene, buildGraphTip, GRAPH_ATTR } from './graphScene.js';
import { findGraphDropTarget } from './graphHitTest.js';
import { nodeGeometry, objectWirePoint } from './graphGeometry.js';
import { graphSnapTargets } from './graphSnap.js';

// Pointer travel (layer px) before a press on a node becomes a move.
const MOVE_THRESHOLD = 3;
// A wire released at least this far from its port on empty canvas opens the
// node picker (as in EEZ Studio); a shorter one is treated as a mis-click.
const PICKER_THRESHOLD = 20;

const CURSORS = { port: 'crosshair', wire: 'pointer', node: 'move' };

/**
 * Overlay layer for the active page's node graph of one domain (Logic, Home
 * Assistant), stacked directly above the object layer. Rebuilt on demand
 * (graphs are small); renders are coalesced to one per animation frame.
 *
 * The layer reads the graph from `objectLayer.activePage[domain.graphKey]`;
 * editor state (visibility, selection) is pushed in through setState().
 * Interaction is tracked here as a preview — a node being moved or a wire
 * being drawn — and only committed on release, through the handlers set with
 * setHandlers(), so a drag is one history entry and re-renders never
 * interrupt it. Hovering a port shows its label and `hint` as a tip, drawn
 * on its own so it does not rebuild the scene.
 *
 * The domain supplies the model and the look:
 *   graphKey, createGraph, getNodeSchema, createContext(page),
 *   canConnect(graph, from, to, context), validate(graph, context),
 *   sources(page) → the page's UI objects (and page) that can carry ports,
 *   objectPorts(node) → [{ id, dir: 'in'|'out', side: 'left'|'right', port }],
 *   and the render fields of buildGraphScene (namePrefix, nodeSize, describeNode, colors, wireColor).
 */
export default class GraphLayer extends Layer {
    constructor({ objectLayer, domain }) {
        super({ listening: false });
        // The app keeps the object layer in reactive state; work on the raw
        // Konva objects so the page graphs stay plain data.
        this.objectLayer = toRaw(objectLayer);
        this.domain = domain;
        // Event namespace of this layer: several graph layers share the stage and object layer.
        this._ns = `graph${domain.graphKey}`;
        this.graphVisible = false;
        this.selection = null;
        this.handlers = {};
        this._frame = null;
        this._interaction = null;
        this._scene = null;
        // The port tip: { key, point, text } and its Konva node.
        this._tip = null;
        this._tipNode = null;
        // Wires to objects follow them while they are dragged or resized.
        this.objectLayer.on(['dragmove', 'transform', 'dragend', 'transformend'].map(name => `${name}.${this._ns}`).join(' '), () => {
            if (this.graphVisible) this.requestRender();
        });
        this.on(`mousedown.${this._ns} touchstart.${this._ns}`, event => this._pointerDown(event));
        this.on(`mouseover.${this._ns}`, event => this._setCursor(this._kindOf(event.target)));
        this.on(`mouseout.${this._ns}`, () => this._setCursor(null));
        this._onPointerMove = event => this._pointerMove(event);
        this._onPointerUp = () => this._pointerUp();
    }

    /**
     * @param {object} handlers
     * @param {(kind: 'node'|'wire'|null, id?: string) => void} handlers.onSelect
     * @param {(from, to) => void} handlers.onConnect
     * @param {(nodeId: string, x: number, y: number) => void} handlers.onMove
     * @param {(drop: { page, dir, endpoint, port, point, client }) => void} handlers.onWireToEmpty
     *        a wire released on empty canvas: `point` is page-local, `client` the screen position
     */
    setHandlers(handlers) {
        this.handlers = handlers ?? {};
        // A press on empty canvas clears the graph selection. Call after the
        // layer was added to the stage.
        const stage = this.getStage();
        stage?.off(`.${this._ns}select`);
        stage?.on(`mousedown.${this._ns}select touchstart.${this._ns}select`, event => {
            if (event.target === stage && this.selection) this.handlers.onSelect?.(null);
        });
        // On the stage, not on the ports: a re-render replaces the hovered port
        // shape, and a replaced shape no longer reports the pointer leaving it.
        stage?.off(`.${this._ns}tip`);
        stage?.on(`mousemove.${this._ns}tip`, () => this._updateTip());
        stage?.on(`mouseleave.${this._ns}tip`, () => this._setTip(null));
    }

    setState({ visible = this.graphVisible, selection = this.selection } = {}) {
        this.graphVisible = Boolean(visible);
        this.selection = selection;
        if (!this.graphVisible) {
            this._endInteraction();
            this._tip = null;
        }
        this.requestRender();
    }

    requestRender() {
        // Hidden and already empty: nothing to draw or clear.
        if (this._frame !== null || (!this.graphVisible && !this.hasChildren())) return;
        const schedule = globalThis.requestAnimationFrame ?? (callback => setTimeout(callback, 16));
        this._frame = schedule(() => {
            this._frame = null;
            this.render();
        });
    }

    _toLocal() {
        const inverse = this.getAbsoluteTransform().copy().invert();
        return point => inverse.point(point);
    }

    /** UI objects of `page` (and the page, when the domain lists it) in this layer's coordinates. */
    _collectObjects(page, toLocal) {
        return this.domain.sources(page)
            .filter(node => node.isVisible?.() !== false)
            .map(node => ({
                node,
                uuid: node.id(),
                rect: { ...toLocal(node.getAbsolutePosition()), width: node.width(), height: node.height() },
                ports: this.domain.objectPorts(node),
            }));
    }

    /** Page-local box of a UI object on the active page. */
    objectPageRect(node) {
        const toPage = this.objectLayer.activePage.getAbsoluteTransform().copy().invert();
        return { ...toPage.point(toRaw(node).getAbsolutePosition()), width: node.width(), height: node.height() };
    }

    /**
     * The active page and, page-local, its UI objects, node shapes and where
     * wires attach to objects, as arrangeGraph takes them. Works while hidden too.
     */
    arrangeInput() {
        const page = this.objectLayer.activePage;
        if (!page) return null;
        const objects = this.domain.sources(page)
            .filter(node => node.isVisible?.() !== false)
            .map(node => ({ uuid: node.id(), rect: this.objectPageRect(node), ports: this.domain.objectPorts(node) }));
        const byUuid = new Map(objects.map(object => [object.uuid, object]));
        return {
            page,
            objects,
            nodeShape: node => {
                const geometry = nodeGeometry(node, { x: 0, y: 0 }, this.domain);
                return { width: geometry.rect.width, height: geometry.rect.height, port: geometry.point };
            },
            objectPort: (uuid, port, dir) => objectWirePoint(byUuid.get(uuid), port, dir),
        };
    }

    /**
     * Where a DOM drag-and-drop event lands: the page-local point, the topmost
     * node of each kind under it (`nodeAt: { [kind]: id }`) and the UI object
     * with ports under it. Works while hidden too.
     */
    dropTarget(domEvent) {
        const stage = this.getStage();
        stage?.setPointersPositions(domEvent);
        const input = this._sceneInput({ force: true });
        if (!input) return null;
        const point = this.getRelativePointerPosition();
        const inside = rect => point.x >= rect.x && point.x <= rect.x + rect.width &&
            point.y >= rect.y && point.y <= rect.y + rect.height;
        const nodeAt = {};
        for (const node of [...input.graph.nodes].reverse()) {
            if (!(node.kind in nodeAt) && inside(nodeGeometry(node, input.origin, this.domain).rect)) nodeAt[node.kind] = node.id;
        }
        const object = [...input.objects].reverse().find(item => item.ports.length > 0 && inside(item.rect));
        return {
            page: input.page,
            point: { x: point.x - input.origin.x, y: point.y - input.origin.y },
            nodeAt,
            objectUuid: object?.uuid ?? null,
        };
    }

    /** Everything a render or a hit test needs, for the active page. */
    _sceneInput({ force = false } = {}) {
        const page = this.objectLayer.activePage;
        if ((!this.graphVisible && !force) || !page) return null;
        const toLocal = this._toLocal();
        const objects = this._collectObjects(page, toLocal);
        return {
            page,
            objects,
            origin: toLocal(page.getAbsolutePosition()),
            graph: page[this.domain.graphKey] ?? this.domain.createGraph(),
            context: this.domain.createContext(page),
        };
    }

    render() {
        this.destroyChildren();
        const input = this._sceneInput();
        this.listening(Boolean(input));
        this._scene = input;
        if (input) {
            const interaction = this._interaction;
            const graph = interaction?.type === 'move' && interaction.position
                ? moveNode(input.graph, interaction.nodeId, interaction.position.x, interaction.position.y)
                : input.graph;
            const selection = this.selection?.pageUuid === input.page.id() ? this.selection : null;
            this.add(buildGraphScene({
                domain: this.domain,
                origin: input.origin,
                graph,
                objects: input.objects,
                selection,
                diagnostics: this.domain.validate(graph, input.context),
                pendingWire: interaction?.type === 'wire' ? this._pendingWire(interaction) : null,
            }));
        }
        this._tipNode = null;
        this._drawTip();
        this.batchDraw();
    }

    /** Show what the port under the pointer is for; nothing while a node moves or a wire is drawn. */
    _updateTip() {
        const scene = this._scene;
        const position = this.getStage()?.getPointerPosition();
        const target = scene && !this._interaction && position ? this.getIntersection(position) : null;
        const dir = target?.getAttr(GRAPH_ATTR.portDir);
        if (!dir) return this._setTip(null);
        const endpoint = this._endpointOf(target);
        const port = this._portOf(scene, endpoint, dir);
        const point = this._toLocal()(target.getAbsolutePosition());
        this._setTip(port?.label ? {
            // The position too: the port moves with its node (undo, arrange).
            key: `${dir}:${endpoint.object ?? endpoint.node}:${endpoint.port}@${Math.round(point.x)},${Math.round(point.y)}`,
            point,
            text: port.hint ? `${port.label}\n${port.hint}` : port.label,
        } : null);
    }

    _setTip(tip) {
        if ((tip?.key ?? null) === (this._tip?.key ?? null)) return;
        this._tip = tip;
        this._drawTip();
        this.batchDraw();
    }

    _drawTip() {
        this._tipNode?.destroy();
        this._tipNode = this._tip && this._scene ? buildGraphTip(this._tip.point, this._tip.text, this.domain.colors) : null;
        if (this._tipNode) this.add(this._tipNode);
    }

    _pendingWire({ dir, startPoint, pointer, drop }) {
        const end = drop?.result.ok ? drop.point : pointer;
        return {
            from: dir === 'out' ? startPoint : end,
            to: dir === 'out' ? end : startPoint,
            pointer,
            valid: drop ? drop.result.ok : null,
            reason: drop?.result.reason,
        };
    }

    _kindOf(target) {
        if (!target || target === this.getStage()) return null;
        if (target.getAttr(GRAPH_ATTR.portDir)) return 'port';
        if (target.getAttr(GRAPH_ATTR.wire)) return 'wire';
        return target.findAncestor?.(`.${this.domain.namePrefix}-node`, true) ? 'node' : null;
    }

    /** What a port shape stands for: `{ object, port }` or `{ node, port }`. */
    _endpointOf(target) {
        const objectUuid = target.getAttr(GRAPH_ATTR.object);
        const port = target.getAttr(GRAPH_ATTR.port);
        return objectUuid ? { object: objectUuid, port } : { node: target.getAttr(GRAPH_ATTR.node), port };
    }

    _setCursor(kind) {
        const container = this.getStage()?.container?.();
        if (container && !this._interaction) container.style.cursor = kind ? CURSORS[kind] : '';
    }

    _pointerDown(event) {
        const target = event.target;
        const kind = this._kindOf(target);
        const scene = this._scene;
        if (!kind || !scene) return;
        // Keep the press away from the stage, which would start panning.
        event.cancelBubble = true;
        const pointer = this.getRelativePointerPosition();

        if (kind === 'port') {
            this._beginInteraction({
                type: 'wire',
                dir: target.getAttr(GRAPH_ATTR.portDir),
                endpoint: this._endpointOf(target),
                startPoint: this._toLocal()(target.getAbsolutePosition()),
                pointer,
                drop: null,
            });
            return;
        }
        if (kind === 'wire') {
            this.handlers.onSelect?.('wire', target.getAttr(GRAPH_ATTR.wire));
            return;
        }
        const nodeId = target.findAncestor(`.${this.domain.namePrefix}-node`, true).getAttr(GRAPH_ATTR.node);
        const node = findNode(scene.graph, nodeId);
        this.handlers.onSelect?.('node', nodeId);
        if (node) {
            this._beginInteraction({
                type: 'move', nodeId, start: pointer, from: { x: node.x, y: node.y }, position: null,
                snap: this._snapSetup(scene, node),
            });
        }
    }

    /** Snap lines and size of a node about to move; null when the stage does no snapping. */
    _snapSetup(scene, node) {
        const stage = this.getStage();
        if (!stage?.snapManager || !stage.snapLayer) return null;
        const targets = graphSnapTargets({
            graph: scene.graph,
            nodeId: node.id,
            origin: scene.origin,
            domain: this.domain,
            objects: scene.objects,
            pageSize: { width: scene.page.width(), height: scene.page.height() },
        });
        const guidelines = stage.guidelineManager?.getSnapTargets();
        if (guidelines) {
            targets.vertical.push(...guidelines.vertical);
            targets.horizontal.push(...guidelines.horizontal);
        }
        const { width, height } = nodeGeometry(node, scene.origin, this.domain).rect;
        return { targets, size: { width, height }, origin: scene.origin };
    }

    /**
     * Page-local position of a moving node, snapped to the lines of its
     * neighbours unless Shift is held (as in EEZ Studio). Shows the matched lines.
     */
    _snapPosition(snap, position, bypass) {
        const stage = this.getStage();
        const rounded = { x: Math.round(position.x), y: Math.round(position.y) };
        if (!snap || !stage?.snapLayer) return rounded;
        const snapLayer = stage.snapLayer;
        snapLayer.clearGuides();
        if (bypass) return rounded;
        const { origin } = snap;
        const result = stage.snapManager.findSnapForContentPos(
            { x: origin.x + position.x, y: origin.y + position.y }, snap.size, snap.targets);
        if (result.guideX !== undefined) snapLayer.drawVerticalGuide(result.guideX, result.guideXyMin, result.guideXyMax);
        if (result.guideY !== undefined) snapLayer.drawHorizontalGuide(result.guideY, result.guideYxMin, result.guideYxMax);
        snapLayer.flushGuides();
        return { x: Math.round(result.snapX - origin.x), y: Math.round(result.snapY - origin.y) };
    }

    _beginInteraction(interaction) {
        this._interaction = interaction;
        this._setTip(null);
        const stage = this.getStage();
        stage?.on(`mousemove.${this._ns}drag touchmove.${this._ns}drag`, this._onPointerMove);
        globalThis.addEventListener?.('mouseup', this._onPointerUp);
        globalThis.addEventListener?.('touchend', this._onPointerUp);
    }

    _endInteraction() {
        if (this._interaction?.snap) this.getStage()?.snapLayer?.clearGuides();
        this._interaction = null;
        this.getStage()?.off(`.${this._ns}drag`);
        globalThis.removeEventListener?.('mouseup', this._onPointerUp);
        globalThis.removeEventListener?.('touchend', this._onPointerUp);
    }

    _pointerMove(event) {
        const interaction = this._interaction;
        const scene = this._scene;
        if (!interaction || !scene) return;
        const pointer = this.getRelativePointerPosition();
        if (interaction.type === 'move') {
            const dx = pointer.x - interaction.start.x;
            const dy = pointer.y - interaction.start.y;
            if (!interaction.position && Math.abs(dx) + Math.abs(dy) < MOVE_THRESHOLD) return;
            interaction.position = this._snapPosition(interaction.snap,
                { x: interaction.from.x + dx, y: interaction.from.y + dy }, event?.evt?.shiftKey);
        } else {
            interaction.pointer = pointer;
            interaction.drop = findGraphDropTarget(pointer, {
                domain: this.domain,
                graph: scene.graph,
                origin: scene.origin,
                objects: scene.objects,
                start: interaction,
                canConnect: (from, to) => this.domain.canConnect(scene.graph, from, to, scene.context),
            });
        }
        this.requestRender();
    }

    _pointerUp() {
        const interaction = this._interaction;
        this._endInteraction();
        if (interaction?.type === 'move' && interaction.position) {
            this.handlers.onMove?.(interaction.nodeId, interaction.position.x, interaction.position.y);
        } else if (interaction?.type === 'wire' && interaction.drop?.result.ok) {
            const { endpoint, drop } = interaction;
            if (interaction.dir === 'out') this.handlers.onConnect?.(endpoint, drop.endpoint);
            else this.handlers.onConnect?.(drop.endpoint, endpoint);
        } else if (interaction?.type === 'wire' && !interaction.drop) {
            this._wireToEmpty(interaction);
        }
        this.requestRender();
    }

    /** Schema port at a wire's open end: an object port or a node port. */
    _portOf(scene, endpoint, dir) {
        if (endpoint.object !== undefined) {
            return scene.objects.find(object => object.uuid === endpoint.object)
                ?.ports.find(port => port.id === endpoint.port)?.port ?? null;
        }
        const ports = resolveNodePorts(this.domain.getNodeSchema, findNode(scene.graph, endpoint.node));
        return (dir === 'out' ? ports?.outputs : ports?.inputs)?.find(port => port.id === endpoint.port) ?? null;
    }

    _wireToEmpty({ dir, endpoint, startPoint, pointer }) {
        const scene = this._scene;
        if (!scene || Math.hypot(pointer.x - startPoint.x, pointer.y - startPoint.y) < PICKER_THRESHOLD) return;
        const stage = this.getStage();
        const box = stage.container().getBoundingClientRect();
        const screen = stage.getPointerPosition();
        this.handlers.onWireToEmpty?.({
            page: scene.page,
            dir,
            endpoint,
            port: this._portOf(scene, endpoint, dir),
            point: { x: pointer.x - scene.origin.x, y: pointer.y - scene.origin.y },
            client: { x: box.left + screen.x, y: box.top + screen.y },
        });
    }

    destroy() {
        this._endInteraction();
        this.getStage()?.off(`.${this._ns}select`);
        this.getStage()?.off(`.${this._ns}tip`);
        this.objectLayer?.off(`.${this._ns}`);
        return super.destroy();
    }
}
